import { typeGuardSelectedInvoice } from '@/components/ReportForm/ApReportFormInputInvoiceFileField/type';
import { useConvertPayeeFormToRequestForm } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndPayeeForm';
import { useConvertSpecialExceptionToRequestForm } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndSpecialExceptionForm';
import { useInvoiceFormTransformers } from '@/context/services/reportsType/invoiceReports/useInvoiceFormTransformers';
import { isSuccess } from '@/context/services/type';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import {
  type AppliedInference,
  CommonError,
  CustomUpdateReportRequest,
  FormInputIdMap,
  InvoiceReportDetailFormInputsResponse,
  useBulkUpdateInvoiceTransactions,
  useBulkUpdateInvoiceTransactionsDraft,
  useGetInvoiceFilesStatusesSuspense,
  useGetInvoiceReportStatusesSuspense,
  useGetInvoiceReportSuspense,
  useGetInvoiceTransactionsSuspense,
  useGetReferenceInvoiceTransactions,
  useGetReferenceRecentApprovedInvoiceReports,
  useUpdateReport,
} from 'ap-openapi';
import { useCallback } from 'react';

export type InvoiceReportStatus = [];

export const useGetInvoiceReport = (invoiceReportsId: string) => {
  const { data, error } = useGetInvoiceReportSuspense(invoiceReportsId);
  if (error) throw error;
  return data.data;
};

export const useGetInvoiceTransaction = (invoiceReportsId: string) => {
  const { data, error } = useGetInvoiceTransactionsSuspense(invoiceReportsId);
  if (error) throw error;
  return data.data?.invoice_transactions ?? [];
};

type TUpdateReportMutateAsync = ReturnType<
  typeof useUpdateReport
>['mutateAsync'];

type UpdateInvoiceReportMutateArgs = Omit<
  Parameters<TUpdateReportMutateAsync>['0'],
  'data'
> & {
  data: CustomUpdateReportRequest;
};

type UpdateInvoiceReportMutateOption =
  Parameters<TUpdateReportMutateAsync>['1'] & TFetchQueries;
type UpdateReportInvoiceFormSuccess = {
  status: 'success';
};
type UpdateReportInvoiceFormCommonError = {
  status: 'error' | 'invalid';
  errors: CommonError;
};
type UpdateReportInvoiceFormResult =
  | UpdateReportInvoiceFormSuccess
  | UpdateReportInvoiceFormCommonError;
const typeGuardUpdateReportRequest = (
  body: unknown
): body is Parameters<TUpdateReportMutateAsync>['0'] => true;

export const useUpdateReportInvoiceForm = () => {
  const { t } = useTranslation();
  const { mutateAsync: _mutateAsync } = useUpdateReport();
  const mutateAsync = useCallback(
    async (
      args: UpdateInvoiceReportMutateArgs,
      options?: UpdateInvoiceReportMutateOption
    ): Promise<UpdateReportInvoiceFormResult> => {
      // NOTE: ファーム変換時に配列情報が失われるのでカスタム型を作成して変換しているのでダミーのタイプガードで騙す
      if (typeGuardUpdateReportRequest(args)) {
        const resp = await _mutateAsync(args, options);
        if (resp.status !== 204) {
          if (resp.status === 422) {
            return {
              status: 'invalid',
              errors: isCommonError(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                resp?.data as unknown as Record<string, unknown>
              )
                ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  (resp.data as unknown as CommonError)
                : { messages: [t('system_error')] },
            };
          }
          return {
            status: 'error',
            errors: isCommonError(
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              resp?.data as unknown as Record<string, unknown>
            )
              ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (resp.data as unknown as CommonError)
              : { messages: [t('system_error')] },
          };
        }
      }
      return {
        status: 'success',
      };
    },
    [_mutateAsync, t]
  );
  return { mutateAsync };
};

type TBulkUpdateInvoiceTransactionsMutateAsync = ReturnType<
  typeof useBulkUpdateInvoiceTransactions
>['mutateAsync'];
type BulkUpdateInvoiceReportMutateArgs =
  Parameters<TBulkUpdateInvoiceTransactionsMutateAsync>['0'];
type BulkUpdateInvoiceReportMutateOption =
  Parameters<TBulkUpdateInvoiceTransactionsMutateAsync>['1'] & TFetchQueries;
type BulkUpdateReportInvoiceTransactionsSuccess = {
  status: 'success';
};
type BulkUpdateReportInvoiceTransactionsCommonError = {
  status: 'error';
  errors: CommonError;
};
type BulkUpdateReportInvoiceTransactionsResult =
  | BulkUpdateReportInvoiceTransactionsSuccess
  | BulkUpdateReportInvoiceTransactionsCommonError;

export const useBulkUpdateReportInvoiceTransactions = () => {
  const { t } = useTranslation();
  const { mutateAsync: _mutateAsync } = useBulkUpdateInvoiceTransactions();
  const mutateAsync = useCallback(
    async (
      args: BulkUpdateInvoiceReportMutateArgs,
      options?: BulkUpdateInvoiceReportMutateOption
    ): Promise<BulkUpdateReportInvoiceTransactionsResult> => {
      const resp = await _mutateAsync(args, options);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      }
      return {
        status: 'error',
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: isCommonError(resp?.data as unknown as Record<string, unknown>)
          ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            (resp.data as unknown as CommonError)
          : { messages: [t('system_error')] },
      };
    },
    [_mutateAsync, t]
  );
  return { mutateAsync };
};

export const useBulkUpdateReportInvoiceTransactionsDraft = () => {
  const { t } = useTranslation();
  const { mutateAsync: _mutateAsync } = useBulkUpdateInvoiceTransactionsDraft();
  const mutateAsync = useCallback(
    async (
      args: BulkUpdateInvoiceReportMutateArgs,
      options?: BulkUpdateInvoiceReportMutateOption
    ): Promise<BulkUpdateReportInvoiceTransactionsResult> => {
      const resp = await _mutateAsync(args, options);
      if (isSuccess(resp.status)) {
        return {
          status: 'success',
        };
      }
      return {
        status: 'error',
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        errors: isCommonError(resp?.data as unknown as Record<string, unknown>)
          ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            (resp.data as unknown as CommonError)
          : { messages: [t('system_error')] },
      };
    },
    [_mutateAsync, t]
  );
  return { mutateAsync };
};

export const useSubmitInvoiceReport = (
  formInputIdMap: FormInputIdMap,
  invoicePayeeTypeInfo: InvoiceReportDetailFormInputsResponse | undefined,
  invoiceFileId: string,
  isHaveSetApPayeeIdByDefault: boolean,
  isDraft: boolean,
  getDateFormatType?: (propertyId: string) => 'year_month' | undefined
) => {
  const { mutateAsync: updateInvoiceReport } = useUpdateReportInvoiceForm();
  const { mutateAsync: bulkUpdateInvoiceTransactions } =
    useBulkUpdateReportInvoiceTransactions();
  const { mutateAsync: bulkUpdateInvoiceTransactionsDraft } =
    useBulkUpdateReportInvoiceTransactionsDraft();
  const { transformReportForm, transformInvoiceTransactions } =
    useInvoiceFormTransformers(formInputIdMap);
  const convertPayeeFormRequest = useConvertPayeeFormToRequestForm();
  const convertSpecialExceptionFormRequest =
    useConvertSpecialExceptionToRequestForm();

  const bulkUpdateFunction = isDraft
    ? bulkUpdateInvoiceTransactionsDraft
    : bulkUpdateInvoiceTransactions;

  return useCallback(
    async (
      values: PaymentRequestForm,
      apInferenceIds?: string[],
      appliedInferences?: AppliedInference[]
    ) => {
      const reportFormData = transformReportForm(values.reportForm);
      const payeeFormData = convertPayeeFormRequest(
        values.payee,
        invoicePayeeTypeInfo,
        Object.values(formInputIdMap).find(
          (input) => input.type === 'ApReportFormInputInvoiceInstantPayee'
        )
      );

      const specialExceptionData = convertSpecialExceptionFormRequest(
        values.specialException
      );
      const reportFormInputValues = [
        ...reportFormData,
        ...payeeFormData,
        ...specialExceptionData,
      ].map((item) => {
        if (item.report_form_input_id === invoiceFileId) {
          if (typeGuardSelectedInvoice(values.invoice_file)) {
            return { ...item, value: values.invoice_file.mfFileId };
          } else if (values.invoice_file instanceof File) {
            return { ...item, file: values.invoice_file, value: undefined };
          } else if (typeof values.invoice_file === 'string') {
            return { ...item, value: values.invoice_file };
          }
        }
        return item;
      });

      const reportFormInputValuesAttributes = reportFormInputValues.sort(
        (a, b) =>
          (formInputIdMap[a.report_form_input_id ?? '']?.priority ?? 0) -
          (formInputIdMap[b.report_form_input_id ?? '']?.priority ?? 0)
      );

      const invoiceReportResponse = await updateInvoiceReport({
        id: values.id,
        data: {
          report: {
            title: values.title,
            report_dept: [
              {
                office_member_dept_id: values.report_dept,
              },
            ],
            report_form_input_values_attributes:
              reportFormInputValuesAttributes,
            ap_inference_ids: apInferenceIds,
            applied_inferences: appliedInferences,
          },
        },
      });

      const hasReportError = invoiceReportResponse?.status === 'invalid';
      /*
       *  invoiceReportResponseの保存に失敗したら明細は保存しないが、すでに支払先が設定済みの場合はエラーが発生しても保存する
       *  If the invoiceReportResponse fails to save, it doesn't save the invoiceReportResponse.
       *  But if the ApPayee.id had been set, it can be saved the InvoiceTransaction even if the invoiceReportResponse fails to save.
       */
      if (hasReportError && !isHaveSetApPayeeIdByDefault) {
        return { invoiceReportResponse, invoiceTransactionResponse: undefined };
      }

      const invoiceTransactionResponse = await bulkUpdateFunction({
        id: values.id,
        data: {
          invoice_transactions: transformInvoiceTransactions(
            values.invoiceTransactions,
            values.deleteInvoiceTransactions,
            getDateFormatType
          ),
        },
      });
      return { invoiceReportResponse, invoiceTransactionResponse };
    },
    [
      transformReportForm,
      convertPayeeFormRequest,
      invoicePayeeTypeInfo,
      formInputIdMap,
      convertSpecialExceptionFormRequest,
      updateInvoiceReport,
      isHaveSetApPayeeIdByDefault,
      bulkUpdateFunction,
      transformInvoiceTransactions,
      getDateFormatType,
      invoiceFileId,
    ]
  );
};

type ResponseError = {
  messages?: string[] | string | undefined;
  code?: string | undefined;
};

export const useReferenceRecentApprovedInvoiceReports = (apPayeeId: string) => {
  const { data, error, isLoading } =
    useGetReferenceRecentApprovedInvoiceReports(
      { ap_payee_id: apPayeeId },
      {
        query: {
          enabled: !!apPayeeId,
        },
        request: {
          headers: {
            agentApplicantResource: 'ap_payee',
          },
        },
      }
    );

  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};

export const useReferenceInvoiceTransactions = (
  invoiceReportsId: string,
  shouldFetch: boolean
) => {
  const { data, error, isLoading } = useGetReferenceInvoiceTransactions(
    invoiceReportsId,
    {
      query: {
        enabled: !!invoiceReportsId && shouldFetch,
      },
      request: {
        headers: {
          agentApplicantResource: 'ap_payee',
        },
      },
    }
  );
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};

export const useGetInvoiceReportStatuses = (isApprovingMode?: boolean) => {
  const { data, error, isLoading } = useGetInvoiceReportStatusesSuspense({
    approving_mode: isApprovingMode ?? true,
  });
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};

export const useGetInvoiceFilesReportStatuses = () => {
  const { data, error, isLoading } = useGetInvoiceFilesStatusesSuspense();
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      error: { ...((data?.data ?? {}) as ResponseError) },
      isLoading,
    };
  }
};
